import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type { HealthCheckCounts, IntegrationHealthCheck } from './integration-health-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL;

/**
 * Safely normalizes an unknown Axios rejection into a proper Error instance.
 * Handles network failures where error.response is undefined without throwing.
 *
 * @param {unknown} error - The caught Axios error
 * @returns {Error} An Error whose .message is the response body string (if any)
 *   or the error's own message. The raw response payload is attached as .payload
 *   for callers that need structured inspection.
 */
function normalizeApiError(error: unknown): Error {
  const axiosError = error as { response?: { data?: unknown }; message?: string };
  const payload = axiosError?.response?.data;
  const message = typeof payload === 'string' ? payload : (axiosError?.message ?? 'An unexpected error occurred');
  const err = new Error(message);
  (err as Error & { payload: unknown }).payload = payload;
  return err;
}

/**
 * Fetches all ACTIVE health check issues for an organization.
 *
 * @param {string} organizationId - The organization UUID
 * @returns {Promise<IntegrationHealthCheck[]>} Array of active health check records
 */
const getHealthChecks = (organizationId: string): Promise<IntegrationHealthCheck[]> => {
  const url = `${baseURL}/api/integration-health-checks/?organization_id=${organizationId}`;
  return axiosInstance
    .get(url)
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeApiError(error)));
};

/**
 * Fetches ACTIVE health check counts grouped by system_access for an organization.
 * Used to drive warning badge counts on the Data Sources tab.
 *
 * @param {string} organizationId - The organization UUID
 * @returns {Promise<HealthCheckCounts>} Map of system_access_id → active issue count
 */
const getHealthCheckCounts = (organizationId: string): Promise<HealthCheckCounts> => {
  const url = `${baseURL}/api/integration-health-checks/counts/?organization_id=${organizationId}`;
  return axiosInstance
    .get(url)
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeApiError(error)));
};

/**
 * Dismisses a health check issue.
 *
 * @param {string} issueId - UUID of the IntegrationHealthCheck to dismiss
 * @returns {Promise<IntegrationHealthCheck>} The updated (dismissed) record
 */
const dismissHealthCheck = (issueId: string): Promise<IntegrationHealthCheck> => {
  const url = `${baseURL}/api/integration-health-checks/dismiss/`;
  return axiosInstance
    .post(url, { issue_id: issueId })
    .then((response) => response.data)
    .catch((error) => Promise.reject(normalizeApiError(error)));
};

export { dismissHealthCheck, getHealthCheckCounts, getHealthChecks };
